'use client';

import { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useAuthGuard } from '@/hooks/useAuthGuard';
import { useTranslation } from '@/hooks/useTranslation';
import { meetingAttendanceApi } from '@/services/api/meeting-attendance';
import { Calendar, MapPin, Users, Clock, QrCode, UserCheck } from 'lucide-react';

const formatTeamsDateTime = (value: string | Date) =>
  new Date(value).toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });

interface Meeting {
  id: string;
  title: string;
  description: string | null;
  startTime: string;
  endTime: string;
  timezone?: string | null;
  location: string | null;
  type: string;
  status: string;
  qrCodeOnline: string | null;
  qrCodeOffline: string | null;
  attendances: Array<{
    id: string;
    status: string;
    user: {
      id: string;
      displayName: string;
    };
  }>;
}

export default function TeamsMeetingsPage() {
  const { isReady, user } = useAuthGuard();
  const { t } = useTranslation();
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [loading, setLoading] = useState(true);
  const [teamsContext, setTeamsContext] = useState<{ user: { id: string; displayName: string; team?: { id: string; displayName: string } } } | null>(null);
  const [teamsReady, setTeamsReady] = useState(false);

  useEffect(() => {
    const teams = (typeof window !== 'undefined' ? (window as any).microsoftTeams : null);
    if (!teams?.app) {
      return;
    }
    const initializeTeams = async () => {
      try {
        await teams.app.initialize();
        setTeamsReady(true);
        const context = await teams.app.getContext();
        setTeamsContext(context);
      } catch (error) {
        console.error('Failed to initialize Teams SDK:', error);
        setTeamsReady(false);
      }
    };
    initializeTeams();
  }, []);

  const fetchMeetings = useCallback(async () => {
    try {
      const data = await meetingAttendanceApi.listMeetings({ status: 'all', limit: 50 });
      setMeetings(data.meetings || []);
    } catch (error) {
      console.error('Failed to fetch meetings list:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isReady) {
      fetchMeetings();
    }
  }, [fetchMeetings, isReady]);

  const handleCheckin = async (meetingId: string) => {
    try {
      await meetingAttendanceApi.checkin(meetingId, { checkinType: 'ONLINE', attendanceStatus: 'ONLINE' });
      const teams = (typeof window !== 'undefined' ? (window as any).microsoftTeams : null);
      if (teams?.app?.notifySuccess) {
        teams.app.notifySuccess(t.meetingAttendance.teamsMeetings.checkinSuccess);
      }
      fetchMeetings();
    } catch (error: any) {
      console.error('Check-in failed:', error);
      const message = error?.response?.data?.error || t.meetingAttendance.teamsMeetings.checkinFailed;
      const teams = (typeof window !== 'undefined' ? (window as any).microsoftTeams : null);
      if (teams?.app?.notifyFailure) {
        teams.app.notifyFailure(message);
      }
    }
  };

  const openQRCode = async (meetingId: string) => {
    const teams = (typeof window !== 'undefined' ? (window as any).microsoftTeams : null);
    if (teamsReady && teams?.pages?.tabs?.navigateToTab) {
      try {
        await teams.pages.tabs.navigateToTab({
          entityId: `qr-${meetingId}`,
          tabName: t.meetingAttendance.teamsMeetings.qrTabName,
        });
        return;
      } catch (error) {
        console.error('Failed to open Teams QR tab:', error);
      }
    }
    window.open(`/meetingattendance/meetings/${meetingId}/qr`, '_blank');
  };

  const getStatusBadge = (status: string) => {
    const statusMap: Record<string, { label: string; color: string }> = {
      SCHEDULED: { label: t.meetingAttendance.status.scheduled, color: 'bg-blue-100 text-blue-800' },
      IN_PROGRESS: { label: t.meetingAttendance.status.inProgress, color: 'bg-green-100 text-green-800' },
      COMPLETED: { label: t.meetingAttendance.status.completed, color: 'bg-gray-100 text-gray-800' },
      CANCELLED: { label: t.meetingAttendance.status.cancelled, color: 'bg-red-100 text-red-800' },
    };

    const info = statusMap[status] || statusMap.SCHEDULED;
    return <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${info.color}`}>{info.label}</span>;
  };

  const getTypeBadge = (type: string) => {
    const typeMap: Record<string, { label: string; color: string }> = {
      OFFLINE: { label: t.meetingAttendance.type.offline, color: 'bg-purple-100 text-purple-800' },
      ONLINE: { label: t.meetingAttendance.type.online, color: 'bg-green-100 text-green-800' },
      HYBRID: { label: t.meetingAttendance.type.hybrid, color: 'bg-yellow-100 text-yellow-800' },
    };
    const info = typeMap[type] || typeMap.OFFLINE;
    return <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${info.color}`}>{info.label}</span>;
  };

  if (!isReady || loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">{t.meetingAttendance.teamsMeetings.loading}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">{t.meetingAttendance.teamsMeetings.title}</h1>
          <p className="text-gray-600">{t.meetingAttendance.teamsMeetings.subtitle}</p>
          {teamsContext && (
            <p className="text-sm text-gray-500 mt-2">
              {t.meetingAttendance.teamsMeetings.currentTeam.replace('{team}', teamsContext.user.team?.displayName || t.meetingAttendance.teamsMeetings.personalTeam)}
            </p>
          )}
        </div>

        <div className="space-y-4">
          {meetings.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Calendar className="h-12 w-12 text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">{t.meetingAttendance.teamsMeetings.emptyTitle}</h3>
                <p className="text-gray-500 text-center">{t.meetingAttendance.teamsMeetings.emptyDescription}</p>
              </CardContent>
            </Card>
          ) : (
            meetings.map((meeting) => {
              const currentUserAttendance = meeting.attendances.find((attendance) => attendance.user.id === (user as any)?.id);
              const canCheckin = ['SCHEDULED', 'IN_PROGRESS'].includes(meeting.status) &&
                (!currentUserAttendance || currentUserAttendance.status === 'ABSENT');

              return (
                <Card key={meeting.id} className="hover:shadow-md transition-shadow">
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <CardTitle className="text-lg">{meeting.title}</CardTitle>
                        {meeting.description && (
                          <CardDescription className="mt-1">{meeting.description}</CardDescription>
                        )}
                      </div>
                      <div className="flex items-center space-x-2">
                        {getStatusBadge(meeting.status)}
                        {getTypeBadge(meeting.type)}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600 mb-4">
                      <div className="flex items-center">
                        <Clock className="mr-2 h-4 w-4" />
                        <div>
                          <div>
                            {t.meetingAttendance.common.start}: {formatTeamsDateTime(meeting.startTime)}
                          </div>
                          <div>
                            {t.meetingAttendance.common.end}: {formatTeamsDateTime(meeting.endTime)}
                          </div>
                        </div>
                      </div>
                      {meeting.location && (
                        <div className="flex items-center">
                          <MapPin className="mr-2 h-4 w-4" />
                          <span>{meeting.location}</span>
                        </div>
                      )}
                      <div className="flex items-center">
                        <Users className="mr-2 h-4 w-4" />
                        <span>{t.meetingAttendance.teamsMeetings.attendanceCount.replace('{count}', String(meeting.attendances.length))}</span>
                      </div>
                      {currentUserAttendance && (
                        <div className="flex items-center">
                          <UserCheck className="mr-2 h-4 w-4" />
                          <span>
                            {t.meetingAttendance.teamsMeetings.statusLabel.replace(
                              '{status}',
                              currentUserAttendance.status === 'ON_SITE' || currentUserAttendance.status === 'ONLINE'
                                ? t.meetingAttendance.teamsMeetings.statusCheckedIn
                                : t.meetingAttendance.teamsMeetings.statusNotCheckedIn,
                            )}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="flex justify-end space-x-2">
                      {canCheckin && (
                        <Button
                          size="sm"
                          onClick={() => handleCheckin(meeting.id)}
                        >
                          <UserCheck className="mr-2 h-4 w-4" />
                          {t.meetingAttendance.teamsMeetings.checkinAction}
                        </Button>
                      )}
                      {(meeting.qrCodeOnline || meeting.qrCodeOffline) && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => void openQRCode(meeting.id)}
                        >
                          <QrCode className="mr-2 h-4 w-4" />
                          {t.meetingAttendance.teamsMeetings.viewQr}
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>
      </div>
      <script
        src="https://res.cdn.office.net/teams-js/2.0.0/js/MicrosoftTeams.min.js"
        async
      ></script>
    </div>
  );
}
